import { createSlice } from '@reduxjs/toolkit';
import { REQUEST_STATUS } from '../constants';
import {
  fetchOrder,
  updateOrder,
  fetchCreateOrderMetadata,
  checkImmunotherapyPermission,
  submitOrder,
} from '../api';

// Initial state
const initialOrderState = {
  // Order form data
  orderData: {
    providerUserUUID: '',
    category: '', // SLIT or SCIT

    // SLIT specific fields
    slitLowDose: false,
    slitLowDoseQuantity: 1,
    slitMaintenance: false,
    slitMaintenanceQuantity: 1,

    // SCIT specific fields
    scitType: '',
    scitAllergens: [],

    // Additional clinical information (for SCIT with immunotherapy report)
    allergySymptoms: [],
    symptomsExposure: [],
    symptomsTimeOfYear: [],
    symptomsTimeInDay: '',
    symptomsMostlyOccur: '',
    examinationReveals: [],
    allergyDiagnosis: [],

    // Common fields
    comments: '',
    address: '',
    patientUuid: '',
  },

  // Loaded order data for reorder
  loadedOrderData: null,

  // Metadata
  metadata: {
    patientInfo: {
      name: '',
      gender: '',
      dateOfBirth: '',
      drugAllergies: '',
    },
    providers: [],
    addresses: [],
  },

  // Permissions
  canEnableImmunotherapyReport: false,

  // Request statuses
  status: REQUEST_STATUS.idle,
  createStatus: REQUEST_STATUS.idle,
  updateStatus: REQUEST_STATUS.idle,
  metadataStatus: REQUEST_STATUS.idle,

  // Errors
  error: null,
  backendErrors: {},
};

export const orderSlice = createSlice({
  name: 'order',
  initialState: initialOrderState,
  reducers: {
    setOrderField: (state, action) => {
      state.orderData[action.payload.field] = action.payload.value;
    },
    setMultipleOrderFields: (state, action) => {
      Object.entries(action.payload).forEach(([key, value]) => {
        state.orderData[key] = value;
      });
    },
    resetOrderForm: state => {
      state.orderData = initialOrderState.orderData;
      state.backendErrors = {};
    },
    resetOrderState: () => initialOrderState,
    setBackendErrors: (state, action) => {
      state.backendErrors = action.payload;
    },
    clearBackendError: (state, action) => {
      delete state.backendErrors[action.payload];
    },
    setCanEnableImmunotherapyReport: (state, action) => {
      state.canEnableImmunotherapyReport = action.payload;
    },
    resetCreateStatus: (state, action) => {
      state.createStatus = REQUEST_STATUS.idle;
    },
    resetUpdateStatus: (state, action) => {
      state.updateStatus = REQUEST_STATUS.idle;
    },
  },
  extraReducers: builder => {
    // Fetch Order
    builder
      .addCase(fetchOrder.pending, state => {
        state.status = REQUEST_STATUS.loading;
        state.error = null;
      })
      .addCase(fetchOrder.fulfilled, (state, action) => {
        state.error = null;
        state.loadedOrderData = action.payload;

        const reorderData = action.payload.data;
        state.orderData = {
          ...state.orderData,
          ...reorderData,
          status: 'pending',
        };

        if (reorderData.category === 'slit') {
          state.orderData.slitMaintenance = true;
          state.orderData.slitMaintenanceQuantity = 3;
          state.orderData.allergySymptoms = [];
          state.orderData.symptomsExposure = [];
          state.orderData.symptomsTimeOfYear = [];
          state.orderData.symptomsTimeInDay = '';
          state.orderData.symptomsMostlyOccur = '';
          state.orderData.examinationReveals = [];
          state.orderData.allergyDiagnosis = [];
        }

        if (reorderData.category === 'scit') {
          state.orderData.scitType = 'twelveDosePerUnitSCITMaintenance';
        }
        state.status = REQUEST_STATUS.succeeded;
      })
      .addCase(fetchOrder.rejected, (state, action) => {
        state.status = REQUEST_STATUS.failed;
        state.error = action.payload;
      });

    // Update Order
    builder
      .addCase(updateOrder.pending, state => {
        state.updateStatus = REQUEST_STATUS.loading;
        state.error = null;
        state.backendErrors = {};
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.updateStatus = REQUEST_STATUS.succeeded;
        state.loadedOrderData = { ...state.loadedOrderData, data: action.payload.data };
        state.error = null;
        state.backendErrors = {};
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.updateStatus = REQUEST_STATUS.failed;
        state.error = action.payload;
        if (action.payload?.errors) {
          state.backendErrors = action.payload.errors;
        }
      });

    // Fetch Create Order Metadata
    builder
      .addCase(fetchCreateOrderMetadata.pending, state => {
        state.metadataStatus = REQUEST_STATUS.loading;
      })
      .addCase(fetchCreateOrderMetadata.fulfilled, (state, action) => {
        state.metadata = action.payload.data;

        if (!state.orderData.providerUserUUID) {
          const { providers, addresses } = action.payload.data;
          state.orderData = {
            providerUserUUID: providers.length ? providers[0].uuid : '',
            category: '',
            scitType: '',
            scitAllergens: [],
            slitLowDose: false,
            slitLowDoseQuantity: 1,
            slitMaintenance: false,
            slitMaintenanceQuantity: 1,
            address: addresses.length ? addresses[0] : {},
            comments: '',
            allergySymptoms: [],
            symptomsExposure: [],
            symptomsTimeOfYear: [],
            symptomsTimeInDay: '',
            symptomsMostlyOccur: '',
            examinationReveals: [],
            allergyDiagnosis: [],
          };
        }

        state.metadataStatus = REQUEST_STATUS.succeeded;
      })
      .addCase(fetchCreateOrderMetadata.rejected, (state, action) => {
        state.error = action.payload;
        state.metadataStatus = REQUEST_STATUS.failed;
      });

    // Submit Order (with UI logic)
    builder
      .addCase(submitOrder.pending, state => {
        state.createStatus = REQUEST_STATUS.loading;
        state.error = null;
        state.backendErrors = {};
      })
      .addCase(submitOrder.fulfilled, (state, action) => {
        state.createStatus = REQUEST_STATUS.succeeded;
        state.error = null;
      })
      .addCase(submitOrder.rejected, (state, action) => {
        state.createStatus = REQUEST_STATUS.failed;
        state.error = action.payload;
        if (action.payload?.errors) {
          state.backendErrors = action.payload.errors;
        }
      });

    // Check Immunotherapy Permission
    builder.addCase(checkImmunotherapyPermission.fulfilled, (state, action) => {
      state.canEnableImmunotherapyReport = action.payload;
    });
  },
});

export const {
  setOrderField,
  setMultipleOrderFields,
  resetOrderForm,
  resetOrderState,
  setBackendErrors,
  clearBackendError,
  setCanEnableImmunotherapyReport,
  resetCreateStatus,
  resetUpdateStatus,
} = orderSlice.actions;

export default orderSlice.reducer;
